// PDF preview modal. R2: deltas (clvDelta / probDelta) MUST NOT
// appear in this surface or any share artefact — only after the
// user reveals them on the live LegCard.
import type { ReactElement } from "react";
import type { Leg, RepairMode, SlipMeta } from "../_stubData.ts";
import { SrIcon, fmtMoney, fmtOdds } from "./SrIcon.tsx";

interface Props {
  meta: SlipMeta;
  legs: Leg[];
  mode: RepairMode;
  finalOdds: number;
  finalPayout: number;
  finalProb: number;
  onClose: () => void;
}

export function PdfPreview({
  meta,
  legs,
  mode,
  finalOdds,
  finalPayout,
  finalProb,
  onClose,
}: Props): ReactElement {
  return (
    <div className="sr-pdf-modal" onClick={onClose}>
      <div className="sr-pdf-wrap" onClick={(e) => e.stopPropagation()}>
        <div className="sr-pdf-toolbar">
          <div className="ttl">
            Repaired Slip · PDF preview
            <span className="ref">strikza-repair-{meta.slipCode}.pdf</span>
          </div>
          <div className="actions">
            <button className="btn" onClick={onClose}>
              <SrIcon name="x" size={12} />
              Close
            </button>
            <button className="btn">
              <SrIcon name="print" size={12} />
              Print
            </button>
            <button className="btn">
              <SrIcon name="share" size={12} />
              Copy share link
            </button>
            <button className="btn primary">
              <SrIcon name="download" size={12} />
              Download
            </button>
          </div>
        </div>

        <div className="sr-pdf-page">
          <div className="sr-pdf-cover">
            <div className="brand">
              <div className="mark">S</div>
              STRIKZA · PROBABILITY INTELLIGENCE
            </div>
            <h1>SLIP REPAIR</h1>
            <div className="subtitle">
              {meta.bookmaker} slip · #{meta.slipCode} · {meta.legCount} legs · stake {meta.currency}
              {fmtMoney(meta.stake)}
            </div>
            <div className="validity-strip">
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C9A84C" }} />
              REPAIRED {meta.repairedAt} · {meta.repairedDate} · ODDS VALID 2 HOURS
            </div>
            <div className="stats">
              <div className="stat-cell">
                <div className="k">Original odds</div>
                <div className="v">{fmtOdds(meta.origOdds)}×</div>
                <div className="sub">{meta.origWinProb.toFixed(2)}% landing probability</div>
              </div>
              <div className="stat-cell">
                <div className="k">{mode === "sharp" ? "Sharp mode" : "Safer mode"} · repaired</div>
                <div className="v sharp">{fmtOdds(finalOdds)}×</div>
                <div className="sub">{finalProb.toFixed(2)}% landing probability</div>
              </div>
              <div className="stat-cell">
                <div className="k">Potential payout</div>
                <div className="v">
                  {meta.currency}
                  {fmtMoney(finalPayout)}
                </div>
                <div className="sub">
                  on {meta.currency}
                  {fmtMoney(meta.stake)} stake
                </div>
              </div>
            </div>
          </div>

          <div className="sr-pdf-body">
            <h2>Leg-by-leg detail</h2>
            {legs.map((leg) => {
              const isReplaced = leg.decision === "replace";
              const isRetained = leg.decision === "retain";
              const isFlagged = leg.decision === "flag";
              const repair = isReplaced && leg.repair ? leg.repair[mode] : null;
              return (
                <div
                  className={`sr-pdf-leg ${isRetained ? "retained" : isFlagged ? "flagged" : ""}`}
                  key={leg.id}
                >
                  <div className="row1">
                    <div className="fx-wrap">
                      <span className="num">L{String(leg.n).padStart(2, "0")}</span>
                      <span className="fx">
                        {leg.fixture.home} vs {leg.fixture.away}
                      </span>
                      <span className="meta">
                        · {leg.league} · {leg.kickoff}
                      </span>
                    </div>
                    <span className="tag">
                      {isRetained
                        ? "RETAINED"
                        : isFlagged
                          ? "REVIEW"
                          : leg.decision === "unresolved"
                            ? "UNRESOLVED"
                            : `REPLACED · ${mode.toUpperCase()}`}
                    </span>
                  </div>
                  {isReplaced && repair ? (
                    <div className="pair">
                      <span>
                        <span className="old">
                          {leg.original.selection} @ {fmtOdds(leg.original.odds)}
                        </span>
                        <br />
                        <span style={{ fontSize: 10, color: "#999" }}>
                          {leg.original.market} · {leg.original.voidRisk} void risk
                        </span>
                      </span>
                      <span className="arrow">→</span>
                      <span>
                        <span className="new">
                          {repair.selection} @ {fmtOdds(repair.odds)}
                        </span>
                        <br />
                        {/* R2: NO delta on PDF/share */}
                        <span style={{ fontSize: 10, color: "#666" }}>{repair.market}</span>
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, marginTop: 4 }}>
                      <strong>{leg.original.selection}</strong> @ {fmtOdds(leg.original.odds)} ·{" "}
                      <span style={{ color: "#888" }}>{leg.original.market}</span>
                    </div>
                  )}
                  <div className="reason">{leg.reasoning.body}</div>
                </div>
              );
            })}
          </div>

          <div className="sr-pdf-foot">
            <span className="strong">Repaired by STRIKZA · Probability Intelligence Platform</span>
            <br />
            Repaired at {meta.repairedAt} on {meta.repairedDate}. Odds subject to movement — verify before placing.
            <br />
            Not financial advice. Bet within your means. 18+. · Referral · strikza.com/r/KA-2206
          </div>
        </div>

        <div style={{ padding: 12, textAlign: "center", fontSize: 11.5, color: "var(--text-tertiary)" }}>
          Page 1 of 1 · A4 · Embeds your referral code · 72-hour share link available
        </div>
      </div>
    </div>
  );
}
